import { useQuery } from '@tanstack/react-query';
import { API_BASE } from '../utils';
import { apiFetch } from './fetch-utils';
import type {
  FplCurrentGameweekResponse,
  FplEntryGwHistoryResponse,
  FplEntryLeaguesResponse,
  FplEntryMarketValueResponse,
  FplEntryMvpsResponse,
  FplEntryNationalitiesResponse,
  FplEntryPicksResponse,
  FplEntryResponse,
  FplEntrySearchResponse,
  FplEntrySeasonHistoryResponse,
  FplFormResponse,
  FplFormStat,
  FplLeagueResponse,
  FplLeagueSearchResponse,
  FplLeagueSquadValueResponse,
  FplLeagueStat,
  FplLeagueStatsResponse,
  FplLeagueTransferManagersResponse,
  FplMvpStat,
  FplNextOpponentResponse,
  FplPlayerDetailResponse,
  FplPlayerSearchResponse,
  FplPosition,
  FplRecentBallersResponse,
  FplStatMetric,
  FplStatsResponse,
  FplVenue,
} from '../types/fpl';

const fetchFplRecentBallers = (limit: number) =>
  apiFetch<FplRecentBallersResponse>(`${API_BASE}/fpl/recent-ballers?limit=${limit}`)

export const useFplRecentBallers = (limit = 10) =>
  useQuery({
    queryKey: ['fplRecentBallers', limit],
    queryFn: () => fetchFplRecentBallers(limit),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

const fetchFplCurrentGameweek = () =>
  apiFetch<FplCurrentGameweekResponse>(`${API_BASE}/fpl/gameweeks/current`)

export const useFplCurrentGameweek = () =>
  useQuery({
    queryKey: ['fplCurrentGameweek'],
    queryFn: fetchFplCurrentGameweek,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

export type FplGameweekSelection = number | 'season'

const fetchFplStats = (
  gameweek: FplGameweekSelection,
  metric: FplStatMetric,
  position: FplPosition | null,
  limit: number
) => {
  const params = new URLSearchParams({ gameweek: String(gameweek), metric, limit: String(limit) })
  if (position) params.set('position', position)
  return apiFetch<FplStatsResponse>(`${API_BASE}/fpl/stats?${params.toString()}`)
}

export const useFplStats = (
  gameweek: FplGameweekSelection | null,
  metric: FplStatMetric,
  position: FplPosition | null,
  limit = 8
) =>
  useQuery({
    queryKey: ['fplStats', gameweek, metric, position, limit],
    queryFn: () => fetchFplStats(gameweek!, metric, position, limit),
    enabled: gameweek != null,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

export class FplApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'FplApiError'
    this.status = status
  }
}

async function throwFplApiError(res: Response): Promise<never> {
  let detail = String(res.status)
  try {
    const body: unknown = await res.json()
    if (typeof body === 'object' && body !== null && typeof (body as { detail?: unknown }).detail === 'string') {
      detail = (body as { detail: string }).detail
    }
  } catch {
    // Body wasn't JSON — fall back to the status-based message above.
  }
  throw new FplApiError(detail, res.status)
}

export async function syncFplEntry(entryId: number): Promise<FplEntryResponse> {
  const res = await fetch(`${API_BASE}/fpl/entries/${entryId}/sync`, { method: 'POST' })
  if (!res.ok) return throwFplApiError(res)
  return res.json()
}

const fetchFplEntryPicks = (entryId: number, event: number) =>
  apiFetch<FplEntryPicksResponse>(`${API_BASE}/fpl/entries/${entryId}/picks?event=${event}`)

export const useFplEntryPicks = (entryId: number | null, event: number | null) =>
  useQuery({
    queryKey: ['fplEntryPicks', entryId, event],
    queryFn: () => fetchFplEntryPicks(entryId!, event!),
    enabled: entryId != null && event != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplPlayerSearch = (q: string) =>
  apiFetch<FplPlayerSearchResponse>(`${API_BASE}/fpl/players/search?q=${encodeURIComponent(q)}`)

export const useFplPlayerSearch = (q: string) =>
  useQuery({
    queryKey: ['fplPlayerSearch', q],
    queryFn: () => fetchFplPlayerSearch(q),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

const fetchFplPlayerDetail = (fplId: number) =>
  apiFetch<FplPlayerDetailResponse>(`${API_BASE}/fpl/players/${fplId}`)

export const useFplPlayerDetail = (fplId: number | null) =>
  useQuery({
    queryKey: ['fplPlayerDetail', fplId],
    queryFn: () => fetchFplPlayerDetail(fplId!),
    enabled: fplId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplPlayerForm = (fplId: number, stat: FplFormStat, venue: FplVenue | null, limit: number) => {
  const params = new URLSearchParams({ stat, limit: String(limit) })
  if (venue) params.set('venue', venue)
  return apiFetch<FplFormResponse>(`${API_BASE}/fpl/players/${fplId}/form?${params.toString()}`)
}

export const useFplPlayerForm = (fplId: number | null, stat: FplFormStat, venue: FplVenue | null, limit = 6) =>
  useQuery({
    queryKey: ['fplPlayerForm', fplId, stat, venue, limit],
    queryFn: () => fetchFplPlayerForm(fplId!, stat, venue, limit),
    enabled: fplId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplNextOpponent = (fplId: number) =>
  apiFetch<FplNextOpponentResponse>(`${API_BASE}/fpl/players/${fplId}/next-opponent`)

export const useFplNextOpponent = (fplId: number | null) =>
  useQuery({
    queryKey: ['fplNextOpponent', fplId],
    queryFn: () => fetchFplNextOpponent(fplId!),
    enabled: fplId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplVsNextOpponent = (fplId: number, stat: FplFormStat, limit: number) => {
  const params = new URLSearchParams({ stat, limit: String(limit) })
  return apiFetch<FplFormResponse>(`${API_BASE}/fpl/players/${fplId}/vs-next-opponent?${params.toString()}`)
}

export const useFplVsNextOpponent = (fplId: number | null, stat: FplFormStat, limit = 5) =>
  useQuery({
    queryKey: ['fplVsNextOpponent', fplId, stat, limit],
    queryFn: () => fetchFplVsNextOpponent(fplId!, stat, limit),
    enabled: fplId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntrySearch = (q: string) =>
  apiFetch<FplEntrySearchResponse>(`${API_BASE}/fpl/entries/search?q=${encodeURIComponent(q)}`)

export const useFplEntrySearch = (q: string) =>
  useQuery({
    queryKey: ['fplEntrySearch', q],
    queryFn: () => fetchFplEntrySearch(q),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

const fetchFplLeagueSearch = (q: string) =>
  apiFetch<FplLeagueSearchResponse>(`${API_BASE}/fpl/leagues/search?q=${encodeURIComponent(q)}`)

export const useFplLeagueSearch = (q: string) =>
  useQuery({
    queryKey: ['fplLeagueSearch', q],
    queryFn: () => fetchFplLeagueSearch(q),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

export async function syncFplLeague(leagueId: number): Promise<FplLeagueResponse> {
  const res = await fetch(`${API_BASE}/fpl/leagues/${leagueId}/sync`, { method: 'POST' })
  if (!res.ok) return throwFplApiError(res)
  return res.json()
}

const fetchFplLeague = (leagueId: number) => apiFetch<FplLeagueResponse>(`${API_BASE}/fpl/leagues/${leagueId}`)

export const useFplLeague = (leagueId: number | null) =>
  useQuery({
    queryKey: ['fplLeague', leagueId],
    queryFn: () => fetchFplLeague(leagueId!),
    enabled: leagueId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

export type FplLeagueGameweekSelection = number | 'season'

const fetchFplLeagueStats = (
  leagueId: number,
  stat: FplLeagueStat,
  gameweek: FplLeagueGameweekSelection,
  page: number,
  limit: number
) => {
  const params = new URLSearchParams({ stat, gameweek: String(gameweek), page: String(page), limit: String(limit) })
  return apiFetch<FplLeagueStatsResponse>(`${API_BASE}/fpl/leagues/${leagueId}/stats?${params.toString()}`)
}

export const useFplLeagueStats = (
  leagueId: number | null,
  stat: FplLeagueStat,
  gameweek: FplLeagueGameweekSelection,
  page: number,
  limit: number
) =>
  useQuery({
    queryKey: ['fplLeagueStats', leagueId, stat, gameweek, page, limit],
    queryFn: () => fetchFplLeagueStats(leagueId!, stat, gameweek, page, limit),
    enabled: leagueId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplLeagueEntrySquadValue = (leagueId: number, entryId: number, gameweek: number) =>
  apiFetch<FplLeagueSquadValueResponse>(
    `${API_BASE}/fpl/leagues/${leagueId}/entries/${entryId}/squad-value?gameweek=${gameweek}`
  )

export const useFplLeagueEntrySquadValue = (
  leagueId: number | null,
  entryId: number | null,
  gameweek: number,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['fplLeagueEntrySquadValue', leagueId, entryId, gameweek],
    queryFn: () => fetchFplLeagueEntrySquadValue(leagueId!, entryId!, gameweek),
    enabled: enabled && leagueId != null && entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplLeaguePlayerTransferManagers = (
  leagueId: number,
  fplId: number,
  gameweek: number,
  direction: 'in' | 'out'
) =>
  apiFetch<FplLeagueTransferManagersResponse>(
    `${API_BASE}/fpl/leagues/${leagueId}/players/${fplId}/transfers?gameweek=${gameweek}&direction=${direction}`
  )

export const useFplLeaguePlayerTransferManagers = (
  leagueId: number | null,
  fplId: number | null,
  gameweek: number,
  direction: 'in' | 'out',
  enabled: boolean
) =>
  useQuery({
    queryKey: ['fplLeaguePlayerTransferManagers', leagueId, fplId, gameweek, direction],
    queryFn: () => fetchFplLeaguePlayerTransferManagers(leagueId!, fplId!, gameweek, direction),
    enabled: enabled && leagueId != null && fplId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntryGwHistory = (entryId: number) =>
  apiFetch<FplEntryGwHistoryResponse>(`${API_BASE}/fpl/entries/${entryId}/gw-history`)

export const useFplEntryGwHistory = (entryId: number | null) =>
  useQuery({
    queryKey: ['fplEntryGwHistory', entryId],
    queryFn: () => fetchFplEntryGwHistory(entryId!),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntrySeasonHistory = (entryId: number) =>
  apiFetch<FplEntrySeasonHistoryResponse>(`${API_BASE}/fpl/entries/${entryId}/season-history`)

export const useFplEntrySeasonHistory = (entryId: number | null) =>
  useQuery({
    queryKey: ['fplEntrySeasonHistory', entryId],
    queryFn: () => fetchFplEntrySeasonHistory(entryId!),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntryMvps = (entryId: number, stat: FplMvpStat, limit: number) =>
  apiFetch<FplEntryMvpsResponse>(`${API_BASE}/fpl/entries/${entryId}/mvps?stat=${stat}&limit=${limit}`)

export const useFplEntryMvps = (entryId: number | null, stat: FplMvpStat, limit = 4) =>
  useQuery({
    queryKey: ['fplEntryMvps', entryId, stat, limit],
    queryFn: () => fetchFplEntryMvps(entryId!, stat, limit),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntryNationalities = (entryId: number, gameweek: number | null) => {
  const params = gameweek != null ? `?gameweek=${gameweek}` : ''
  return apiFetch<FplEntryNationalitiesResponse>(`${API_BASE}/fpl/entries/${entryId}/nationalities${params}`)
}

export const useFplEntryNationalities = (entryId: number | null, gameweek: number | null) =>
  useQuery({
    queryKey: ['fplEntryNationalities', entryId, gameweek],
    queryFn: () => fetchFplEntryNationalities(entryId!, gameweek),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntryLeagues = (entryId: number, gameweek: FplLeagueGameweekSelection) =>
  apiFetch<FplEntryLeaguesResponse>(`${API_BASE}/fpl/entries/${entryId}/leagues?gameweek=${gameweek}`)

export const useFplEntryLeagues = (entryId: number | null, gameweek: FplLeagueGameweekSelection) =>
  useQuery({
    queryKey: ['fplEntryLeagues', entryId, gameweek],
    queryFn: () => fetchFplEntryLeagues(entryId!, gameweek),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })

const fetchFplEntryMarketValue = (entryId: number, gameweek: number | null) => {
  const params = gameweek != null ? `?gameweek=${gameweek}` : ''
  return apiFetch<FplEntryMarketValueResponse>(`${API_BASE}/fpl/entries/${entryId}/market-value${params}`)
}

export const useFplEntryMarketValue = (entryId: number | null, gameweek: number | null) =>
  useQuery({
    queryKey: ['fplEntryMarketValue', entryId, gameweek],
    queryFn: () => fetchFplEntryMarketValue(entryId!, gameweek),
    enabled: entryId != null,
    staleTime: 60 * 1000,
    retry: 1,
  })
